import React, { useEffect, useRef, useState } from "react";

import { ReactComponent as ChevronDownSvg } from "@assets/Icons/IconChevron-Down.svg";
import MobileBottomSheet from "./MobileBottomSheet";
import PanelSpinner from "./PanelSpinner";
import { usePanelReveal } from "../../hooks/usePanelReveal";
import {
  CHEVRON_ICON,
  CHROME_LOGO,
  CHROME_PRODUCT_LABEL,
  CHROME_TRIGGER,
  FOCUS_RING,
  PANEL_OPEN_DOWN,
  RAIL_WIDTH_EXPANDED,
  ROW_GAP,
  ROW_ICON,
  ROW_PADDING,
  ROW_RADIUS,
  ROW_TEXT,
  TRANSITION_COLORS,
  TRANSITION_TRANSFORM,
} from "../../sideMenuDensity";

export type SideMenuAccountCardProps = {
  logoSrc: string;
  onLogoError: () => void;
  productOptions: string[];
  selectedProduct: string;
  onSelectProduct: (value: string) => void;
  /** Icon-only trigger for the collapsed sidebar. */
  compact?: boolean;
  /** Mobile drawer variant: full-width trigger in the drawer row, and the card
   *  content opens as a bottom sheet (portal) instead of an absolute dropdown. */
  mobile?: boolean;
};

/**
 * Holds the product list behind a centred spinner during the panel's open reveal.
 *
 * The list stays rendered underneath, `invisible`: `visibility: hidden` keeps its
 * box, so the panel is exactly as tall while the spinner shows as it is once the
 * list appears. Reserving that height any other way would mean hard-coding a row
 * height here. It also drops the rows from the tab order, and `aria-hidden` keeps
 * them out of the accessibility tree until they are real.
 *
 * The tenant selector needs no equivalent — its columns are a fixed height, so
 * its spinner can simply take the list's place.
 */
function PanelRevealOverlay({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative">
      <div className="invisible" aria-hidden>
        {children}
      </div>
      <div className="absolute inset-0 flex items-center justify-center">
        {/* Silent: nothing is loading, so nothing is announced. */}
        <PanelSpinner className="h-5 w-5" decorative />
      </div>
    </div>
  );
}

/**
 * The rail's product switcher: a trigger showing the brand logo and the selected
 * product, opening a radio-style list of the rest.
 *
 * Product only (CTX-655). Identity, Display, Admin center and Log out used to
 * hang off the same control; they live behind the header's avatar now, and
 * keeping a copy here would have been the same thing in two places. On desktop
 * the list is an absolute dropdown; on mobile (`mobile`) it opens as a
 * full-width bottom sheet. Partner/customer selection lives in the separate
 * tenant card (SideMenuTenantCard).
 */
export default function SideMenuAccountCard({
  logoSrc,
  onLogoError,
  productOptions,
  selectedProduct,
  onSelectProduct,
  compact = false,
  mobile = false,
}: SideMenuAccountCardProps) {
  const [cardOpen, setCardOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  /* Covers both shells: the desktop panel is unmounted while closed, but the
     mobile sheet stays mounted and only toggles, so the reveal keys off the open
     flag rather than off mounting. */
  const revealingProducts = usePanelReveal(cardOpen);

  const closeCard = () => setCardOpen(false);

  // Close on outside click.
  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      // On mobile the content is a bottom sheet (portaled outside rootRef); its
      // own backdrop handles closing, so skip the window outside-click there.
      if (mobile || !cardOpen) return;
      if (rootRef.current?.contains(e.target as Node)) return;
      closeCard();
    };
    window.addEventListener("pointerdown", onPointerDown, { capture: true });
    return () =>
      window.removeEventListener(
        "pointerdown",
        onPointerDown as EventListener,
        { capture: true } as EventListenerOptions
      );
  }, [cardOpen, mobile]);

  // Close on Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeCard();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /* Extracted so the reveal can render the very same list underneath its
     spinner — that is what reserves the exact height (see PanelRevealOverlay). */
  const productList = (
    <div className="px-2 py-2" role="radiogroup" aria-label="Product">
      {productOptions.map((p) => {
        const active = p === selectedProduct;
        return (
          <button
            key={p}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => {
              onSelectProduct(p);
              closeCard();
            }}
            className={`flex w-full items-center ${ROW_GAP} ${ROW_RADIUS} ${ROW_PADDING} text-left ${ROW_TEXT} ${TRANSITION_COLORS} ${FOCUS_RING} ${
              active
                ? "font-semibold text-semantic-text-brand"
                : "text-semantic-text-secondary hover:bg-semantic-state-active hover:text-semantic-text-brand"
            }`}
          >
            {/* `bg-current` ties the dot to the row's own ink, so it is the same
                teal as the label beside it in both themes rather than a second
                hardcoded brand colour that could drift from it. */}
            <span
              className={`flex ${ROW_ICON} shrink-0 items-center justify-center`}
              aria-hidden
            >
              <span
                className={
                  active
                    ? "h-1.5 w-1.5 rounded-full bg-current"
                    : "h-1.5 w-1.5 rounded-full ring-1 ring-semantic-border-secondary"
                }
              />
            </span>
            <span className="truncate">{p}</span>
          </button>
        );
      })}
    </div>
  );

  const cardBody = (
    <>
      {/* Product list. A radio dot (CTX-335), not the target glyph this used:
          the glyph rendered on every row and only changed colour, so "which
          product am I in" was carried by a hue difference alone. A filled dot
          against an empty ring reads as chosen/not-chosen by shape as well, which
          also survives the accent being unavailable to a colour-blind reader. */}
      {revealingProducts ? (
        <PanelRevealOverlay>{productList}</PanelRevealOverlay>
      ) : (
        productList
      )}

    </>
  );

  return (
    /* `min-w-0` so the trigger inside can actually shrink: this element is itself
       a flex item of the chrome row, and without it the browser floors it at the
       product name's intrinsic width and the truncation above never runs.
       `flex-1` only on the wide variants — the collapsed rail wants the intrinsic
       32px square, not a stretched one. */
    <div
      ref={rootRef}
      className={compact ? "relative" : "relative min-w-0 flex-1"}
    >
      {/* Trigger: logo + selected product + chevron. Borderless on purpose
          (CTX-335): the button's fill already resolved to the rail's own
          `bg-semantic-bg-primary`, so the 1px border was the only thing making
          it read as a pill floating *on* the rail instead of as the rail's own
          heading. Both are dropped, leaving hover as the sole affordance.

          Deliberately NOT applied to its neighbours: the collapse/expand toggle
          in `SideMenuTopChrome` keeps its border, and so does the dropdown panel
          below. This is the product switcher only — do not "unify" the three. */}
      <button
        type="button"
        onClick={() => setCardOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={cardOpen}
        aria-label="Account menu"
        /* `w-full min-w-0` on every wide variant, not just mobile: on desktop the
           trigger sat in a flex row beside the collapse toggle with no width
           constraint, so a long product name pushed the toggle off the rail
           instead of engaging the label's `truncate` (CTX-335 AC). A flex item
           will not shrink below its content unless `min-w-0` says it may. */
        className={
          compact
            ? `flex h-8 w-8 items-center justify-center rounded-md ${TRANSITION_COLORS} ${FOCUS_RING} hover:bg-semantic-state-active`
            : `flex ${CHROME_TRIGGER} w-full min-w-0 items-center gap-2 rounded-md px-2 ${TRANSITION_COLORS} ${FOCUS_RING} hover:bg-semantic-state-active`
        }
      >
        {compact ? (
          <img
            src={logoSrc}
            alt="Brand logo"
            className="max-h-5 max-w-full object-contain text-semantic-text-primary"
            onError={onLogoError}
          />
        ) : (
          <>
            <span
              className={`flex ${CHROME_LOGO} shrink-0 items-center justify-center overflow-hidden rounded bg-semantic-bg-secondary`}
            >
              <img
                src={logoSrc}
                alt="Brand logo"
                className="max-h-full max-w-full object-contain text-semantic-text-primary"
                onError={onLogoError}
              />
            </span>
            <span
              className={`min-w-0 flex-1 truncate text-left ${CHROME_PRODUCT_LABEL} font-normal text-semantic-text-primary`}
              title={selectedProduct}
            >
              {selectedProduct}
            </span>
            <ChevronDownSvg
              className={`${CHEVRON_ICON} shrink-0 text-semantic-text-secondary ${TRANSITION_TRANSFORM} ${
                cardOpen ? "rotate-180" : ""
              }`}
            />
          </>
        )}
      </button>

      {mobile ? (
        <MobileBottomSheet open={cardOpen} onClose={closeCard}>
          {cardBody}
        </MobileBottomSheet>
      ) : (
        cardOpen && (
          /* Bound to the rail width so the panel always sits flush with it. It was
             a fixed `w-72` (288px), which fit inside the old 304px rail but would
             overhang the narrowed one and read as a misalignment (CTX-242). */
          <div
            className={`absolute left-0 top-full z-[60] mt-2 ${RAIL_WIDTH_EXPANDED} overflow-hidden rounded-lg border border-semantic-border-secondary bg-semantic-bg-primary shadow-xl ${PANEL_OPEN_DOWN}`}
          >
            {cardBody}
          </div>
        )
      )}
    </div>
  );
}
